/**
 * Push Notification Utilities (web)
 * Handles permission requests, token registration, and service worker setup
 * using the standard Web Push API.
 *
 * AI-4697-4700: Push notification setup (frontend + backend)
 * AI-11994: Capacitor wrapper retired — this codebase is web-only now; native
 * push lives in the Expo app (mobile/) via expo-notifications.
 */

import { API_URL } from '@/lib/config';

// ============================================
// SERVICE WORKER REGISTRATION
// ============================================

/**
 * Register the service worker and return the registration.
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    console.warn('Service workers not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
    });
    console.log('Service worker registered:', registration.scope);
    return registration;
  } catch (err) {
    console.error('Service worker registration failed:', err);
    return null;
  }
}

// ============================================
// PUSH NOTIFICATION PERMISSION
// ============================================

/**
 * Check if push notifications are supported.
 */
export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/**
 * Get current notification permission state.
 */
export function getNotificationPermission(): NotificationPermission {
  if (!('Notification' in window)) return 'denied';
  return Notification.permission;
}

/**
 * Request notification permission from the user.
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) return 'denied';
  return Notification.requestPermission();
}

// ============================================
// PUSH SUBSCRIPTION
// ============================================

/**
 * Subscribe to push notifications and register the token with the backend.
 * Returns the push subscription or null on failure.
 */
export async function subscribeToPush(
  getAuthToken: () => Promise<string | null>
): Promise<PushSubscription | null> {
  try {
    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      console.warn('Notification permission not granted');
      return null;
    }

    const registration = await registerServiceWorker();
    if (!registration) return null;

    // Check for existing subscription
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      // Get VAPID public key from server
      const vapidRes = await fetch(`${API_URL}/push/vapid-key`);
      if (!vapidRes.ok) {
        console.warn('Failed to fetch VAPID key — push setup skipped');
        return null;
      }
      const { publicKey } = await vapidRes.json();

      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey).buffer as ArrayBuffer,
      });
    }

    // Register subscription with backend
    const token = await getAuthToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    await fetch(`${API_URL}/push/register`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        subscription: subscription.toJSON(),
        platform: detectPlatform(),
      }),
    });

    return subscription;
  } catch (err) {
    console.error('Push subscription failed:', err);
    return null;
  }
}

/**
 * Unsubscribe from push notifications.
 */
export async function unsubscribeFromPush(): Promise<boolean> {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      await subscription.unsubscribe();
      return true;
    }
    return false;
  } catch (err) {
    console.error('Push unsubscribe failed:', err);
    return false;
  }
}

// ============================================
// UNIFIED PUSH INITIALIZATION
// ============================================

/**
 * Remove native push notification listeners.
 *
 * No-op since the Capacitor shell was retired (AI-11994) — kept so existing
 * call sites (AuthContext sign-out cleanup) keep working unchanged. Web push
 * subscriptions are managed via {@link unsubscribeFromPush}.
 */
export async function removeNativePushListeners(): Promise<void> {
  // Intentionally empty — no native listeners exist on the web.
}

/**
 * Initialize push notifications using the Web Push API with service worker.
 * (Native push is handled by the Expo app in mobile/, not this codebase.)
 */
export async function initPushNotifications(
  getAuthToken: () => Promise<string | null>
): Promise<void> {
  await subscribeToPush(getAuthToken);
}

// ============================================
// HELPERS
// ============================================

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function detectPlatform(): string {
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  if (/android/i.test(ua)) return 'android';
  return 'web';
}
